import katex from "katex";
import { h, clear } from "@/lib/dom";
import type { Answer } from "./scoring";
import type { CheckResult, Question } from "./types";

/**
 * Rendering der neun Aufgabentypen. Jeder Renderer liefert dieselbe kleine
 * Schnittstelle zurück, damit die Quiz-Seite nichts über den konkreten Typ
 * wissen muss: Element, „kann geprüft werden?", Antwort auslesen, Auflösung
 * anzeigen, sperren.
 *
 * Bewertet wird hier nichts – das steht vollständig in scoring.ts.
 */

export interface MountedQuestion {
  el: HTMLElement;
  ready(): boolean;
  read(): Answer;
  reveal(res: CheckResult): void;
  lock(): void;
}

/** Kleine Markdown-Teilmenge: **fett**, *kursiv*, `code`. Kein HTML von außen. */
/** LaTeX zwischen $...$ (KI-Aufgaben nutzen das für Formeln) durch renderten Platzhalter ersetzen, VOR dem HTML-Escaping. */
function withMath(text: string, formulas: string[]): string {
  return (text ?? "").replace(/\$([^$\n]+)\$/g, (_m, latex: string) => {
    try {
      const html = katex.renderToString(latex.replace(/(\d),(\d)/g, "$1{,}$2"), { throwOnError: false });
      formulas.push(html);
      return `\u0000${formulas.length - 1}\u0000`;
    } catch {
      return `$${latex}$`;
    }
  });
}

export function inline(text: string): string {
  const formulas: string[] = [];
  const withPlaceholders = withMath(text, formulas);
  const esc = withPlaceholders
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const formatted = esc
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  return formatted.replace(/\u0000(\d+)\u0000/g, (_m, i) => formulas[Number(i)] ?? "");
}

function shuffled<T>(list: T[]): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const KEYS = ["1", "2", "3", "4", "5", "6"];

/* ------------------------------- Dispatcher ------------------------------- */

export function mountQuestion(q: Question, onChange: () => void): MountedQuestion {
  switch (q.type) {
    case "info":     return infoQ(q);
    case "single":   return singleQ(q, onChange);
    case "multiple": return multipleQ(q, onChange);
    case "fill":     return fillQ(q, onChange);
    case "match":    return matchQ(q, onChange);
    case "calc":     return calcQ(q, onChange);
    case "error":    return errorQ(q, onChange);
    case "code":     return codeQ(q, onChange);
    case "table":    return tableQ(q, onChange);
  }
}

/* --------------------------------- info --------------------------------- */

function infoQ(q: Extract<Question, { type: "info" }>): MountedQuestion {
  const el = h(
    "div",
    { class: "pf-info" },
    h("h2", { class: "pf-q-title" }, q.title),
    h(
      "div",
      { class: "pf-info-card" },
      ...q.body.map((p) => h("p", { class: "pf-info-body", html: inline(p) })),
      q.box ? h("pre", { class: "pf-code", html: inline(q.box) }) : null
    )
  );
  return { el, ready: () => true, read: () => ({}), reveal: () => {}, lock: () => {} };
}

/* -------------------------------- single -------------------------------- */

function singleQ(q: Extract<Question, { type: "single" }>, onChange: () => void): MountedQuestion {
  let picked = -1;
  const order = shuffled(q.options.map((text, i) => ({ text, i })));

  const buttons = order.map((o, pos) => {
    const btn = h(
      "button",
      {
        class: "pf-opt",
        type: "button",
        onclick: () => {
          picked = o.i;
          buttons.forEach((b) => b.classList.remove("sel"));
          btn.classList.add("sel");
          onChange();
        },
      },
      h("span", { class: "pf-key" }, KEYS[pos] ?? "•"),
      h("span", { html: inline(o.text) })
    );
    return btn;
  });

  const el = h(
    "div",
    null,
    h("h2", { class: "pf-q-title", html: inline(q.question) }),
    h("div", { class: "pf-options" }, ...buttons)
  );

  return {
    el,
    ready: () => picked >= 0,
    read: () => ({ index: picked }),
    reveal: () => {
      order.forEach((o, pos) => {
        if (o.i === q.correct) buttons[pos].classList.add("good");
        else if (o.i === picked) buttons[pos].classList.add("bad");
      });
    },
    lock: () => buttons.forEach((b) => (b.disabled = true)),
  };
}

/* ------------------------------- multiple ------------------------------- */

function multipleQ(q: Extract<Question, { type: "multiple" }>, onChange: () => void): MountedQuestion {
  const picked = new Set<number>();
  const order = shuffled(q.options.map((text, i) => ({ text, i })));

  const buttons = order.map((o, pos) => {
    const btn = h(
      "button",
      {
        class: "pf-opt",
        type: "button",
        onclick: () => {
          if (picked.has(o.i)) picked.delete(o.i);
          else picked.add(o.i);
          btn.classList.toggle("sel", picked.has(o.i));
          onChange();
        },
      },
      h("span", { class: "pf-key square" }, KEYS[pos] ?? "•"),
      h("span", { html: inline(o.text) })
    );
    return btn;
  });

  const el = h(
    "div",
    null,
    h("h2", { class: "pf-q-title", html: inline(q.question) }),
    h("p", { class: "pf-q-hint" }, "Mehrere Antworten sind richtig."),
    h("div", { class: "pf-options" }, ...buttons)
  );

  return {
    el,
    ready: () => picked.size > 0,
    read: () => ({ indices: [...picked] }),
    reveal: () => {
      order.forEach((o, pos) => {
        if (q.correct.includes(o.i)) buttons[pos].classList.add("good");
        else if (picked.has(o.i)) buttons[pos].classList.add("bad");
      });
    },
    lock: () => buttons.forEach((b) => (b.disabled = true)),
  };
}

/* --------------------------------- fill --------------------------------- */

function fillQ(q: Extract<Question, { type: "fill" }>, onChange: () => void): MountedQuestion {
  const filled: (string | null)[] = q.blanks.map(() => null);
  const slots: HTMLElement[] = [];
  let active = 0;

  const bankWords = shuffled([...q.blanks, ...(q.distractors ?? [])]);
  const chips: HTMLButtonElement[] = [];

  /** Erste noch leere Lücke – dahin wandert der nächste Klick. */
  function nextEmpty(): number {
    const i = filled.findIndex((v) => v === null);
    return i === -1 ? 0 : i;
  }

  function paintSlots(): void {
    slots.forEach((slot, i) => {
      clear(slot);
      slot.classList.toggle("filled", filled[i] !== null);
      slot.classList.toggle("active", i === active);
      if (filled[i] !== null) slot.appendChild(h("span", { class: "pf-chip in" }, filled[i]!));
    });
    // Ein Baustein darf nur so oft benutzt werden, wie er im Vorrat liegt.
    const used = new Map<string, number>();
    for (const v of filled) if (v) used.set(v, (used.get(v) ?? 0) + 1);
    const seen = new Map<string, number>();
    chips.forEach((chip) => {
      const word = chip.textContent ?? "";
      const n = (seen.get(word) ?? 0) + 1;
      seen.set(word, n);
      chip.classList.toggle("used", n <= (used.get(word) ?? 0));
    });
  }

  const parts = q.text.split(/(\{\{\d+\}\})/g);
  const textEl = h("div", { class: "pf-fill" });
  for (const part of parts) {
    const m = part.match(/^\{\{(\d+)\}\}$/);
    if (!m) {
      textEl.appendChild(h("span", { html: inline(part) }));
      continue;
    }
    const idx = Number(m[1]) - 1;
    const slot = h("span", {
      class: "pf-blank",
      onclick: () => {
        if (filled[idx] !== null) {
          filled[idx] = null;
          active = idx;
        } else {
          active = idx;
        }
        paintSlots();
        onChange();
      },
    });
    slots[idx] = slot;
    textEl.appendChild(slot);
  }

  for (const word of bankWords) {
    const chip = h(
      "button",
      {
        class: "pf-chip",
        type: "button",
        onclick: () => {
          const target = filled[active] === null ? active : nextEmpty();
          filled[target] = word;
          active = nextEmpty();
          paintSlots();
          onChange();
        },
      },
      word
    );
    chips.push(chip);
  }

  paintSlots();

  const el = h(
    "div",
    null,
    h("h2", { class: "pf-q-title", html: inline(q.question) }),
    textEl,
    h("div", { class: "pf-bank" }, ...chips)
  );

  return {
    el,
    ready: () => filled.every((v) => v !== null),
    read: () => ({ texts: filled.map((v) => v ?? "") }),
    reveal: () => {
      slots.forEach((slot, i) => {
        const good = (filled[i] ?? "").trim().toLowerCase() === q.blanks[i].trim().toLowerCase();
        slot.classList.add(good ? "good" : "bad");
        if (!good) {
          clear(slot);
          slot.appendChild(h("span", { class: "pf-chip in" }, q.blanks[i]));
        }
      });
    },
    lock: () => {
      chips.forEach((c) => (c.disabled = true));
      slots.forEach((s) => s.classList.add("locked"));
    },
  };
}

/* --------------------------------- match --------------------------------- */

function matchQ(q: Extract<Question, { type: "match" }>, onChange: () => void): MountedQuestion {
  const left = q.pairs.map(([a], i) => ({ text: a, i }));
  const right = shuffled(q.pairs.map(([, b], i) => ({ text: b, i })));
  let selectedLeft: { tile: HTMLButtonElement; i: number } | null = null;
  let solved = 0;
  let mistakes = 0;

  function tile(text: string, index: number, side: "l" | "r"): HTMLButtonElement {
    return h(
      "button",
      {
        class: "pf-tile",
        type: "button",
        onclick: () => pick(side, index),
      },
      h("span", { html: inline(text) })
    ) as HTMLButtonElement;
  }

  const leftTiles = left.map((o) => tile(o.text, o.i, "l"));
  const rightTiles = right.map((o) => tile(o.text, o.i, "r"));

  function pick(side: "l" | "r", index: number): void {
    const btn = side === "l"
      ? leftTiles[left.findIndex((o) => o.i === index)]
      : rightTiles[right.findIndex((o) => o.i === index)];
    if (btn.classList.contains("done")) return;

    if (side === "l") {
      leftTiles.forEach((t) => t.classList.remove("sel"));
      btn.classList.add("sel");
      selectedLeft = { tile: btn, i: index };
      return;
    }

    if (!selectedLeft) {
      rightTiles.forEach((t) => t.classList.remove("sel"));
      btn.classList.add("sel");
      return;
    }

    if (selectedLeft.i === index) {
      selectedLeft.tile.classList.remove("sel");
      selectedLeft.tile.classList.add("done");
      btn.classList.add("done");
      selectedLeft = null;
      solved++;
      onChange();
    } else {
      mistakes++;
      btn.classList.add("bad");
      selectedLeft.tile.classList.add("bad");
      const wrongLeft = selectedLeft.tile;
      selectedLeft = null;
      window.setTimeout(() => {
        btn.classList.remove("bad");
        wrongLeft.classList.remove("bad", "sel");
      }, 420);
      onChange();
    }
  }

  const el = h(
    "div",
    null,
    h("h2", { class: "pf-q-title", html: inline(q.question) }),
    h(
      "div",
      { class: "pf-match" },
      h("div", { class: "pf-col" }, ...leftTiles),
      h("div", { class: "pf-col" }, ...rightTiles)
    )
  );

  return {
    el,
    ready: () => solved >= q.pairs.length,
    read: () => ({ pairsSolved: solved, pairsTotal: q.pairs.length, mistakes }),
    reveal: () => {},
    lock: () => [...leftTiles, ...rightTiles].forEach((t) => (t.disabled = true)),
  };
}

/* --------------------------------- calc --------------------------------- */

function calcQ(q: Extract<Question, { type: "calc" }>, onChange: () => void): MountedQuestion {
  const input = h("input", {
    class: "pf-input",
    type: "text",
    inputmode: "decimal",
    placeholder: "Ergebnis …",
    autocomplete: "off",
    oninput: onChange,
  }) as HTMLInputElement;

  const el = h(
    "div",
    null,
    h("h2", { class: "pf-q-title", html: inline(q.question) }),
    h(
      "div",
      { class: "pf-input-row" },
      input,
      q.unit ? h("span", { class: "pf-unit" }, q.unit) : null
    )
  );

  return {
    el,
    ready: () => input.value.trim().length > 0,
    read: () => ({ text: input.value }),
    reveal: (res: CheckResult) => {
      input.classList.add(res.verdict === "correct" ? "good" : res.verdict === "partial" ? "soft" : "bad");
    },
    lock: () => (input.disabled = true),
  };
}

/* --------------------------------- error --------------------------------- */

function errorQ(q: Extract<Question, { type: "error" }>, onChange: () => void): MountedQuestion {
  let picked = -1;
  const lineEls = q.lines.map((text, i) =>
    h(
      "span",
      {
        class: "pf-line",
        onclick: () => {
          picked = i;
          lineEls.forEach((l) => l.classList.remove("sel"));
          lineEls[i].classList.add("sel");
          onChange();
        },
      },
      text || " "
    )
  );

  const fixSlot = h("div", { class: "pf-fix" });

  const el = h(
    "div",
    null,
    h("h2", { class: "pf-q-title", html: inline(q.question) }),
    h("p", { class: "pf-q-hint" }, "Tippe die Zeile an, in der der Fehler steckt."),
    h("div", { class: "pf-code lines" }, ...lineEls),
    fixSlot
  );

  return {
    el,
    ready: () => picked >= 0,
    read: () => ({ index: picked }),
    reveal: () => {
      lineEls[q.badLine]?.classList.add("bad");
      if (picked >= 0 && picked !== q.badLine) lineEls[picked].classList.add("miss");
      fixSlot.appendChild(h("div", { class: "pf-fix-cap" }, "So ist es richtig"));
      fixSlot.appendChild(
        h(
          "div",
          { class: "pf-code fixed" },
          ...q.lines.map((text, i) =>
            h("span", { class: "pf-line" + (i === q.badLine ? " fix" : "") }, i === q.badLine ? q.fixedLine : text || " ")
          )
        )
      );
      requestAnimationFrame(() => fixSlot.classList.add("show"));
    },
    lock: () => lineEls.forEach((l) => l.classList.add("locked")),
  };
}

/* --------------------------------- code --------------------------------- */

function codeQ(q: Extract<Question, { type: "code" }>, onChange: () => void): MountedQuestion {
  const area = h("textarea", {
    class: "pf-editor",
    rows: 4,
    spellcheck: false,
    placeholder: "Deine Antwort …",
    oninput: onChange,
  }) as HTMLTextAreaElement;
  area.value = q.starter ?? "";

  const el = h(
    "div",
    null,
    h("h2", { class: "pf-q-title", html: inline(q.question) }),
    area
  );

  return {
    el,
    ready: () => area.value.trim().length > 0,
    read: () => ({ text: area.value }),
    reveal: (res: CheckResult) => {
      area.classList.add(res.verdict === "correct" ? "good" : res.verdict === "partial" ? "soft" : "bad");
      if (res.verdict !== "correct") {
        el.appendChild(h("div", { class: "pf-solution" }, h("span", null, "Musterlösung: "), h("code", null, q.expected)));
      }
    },
    lock: () => (area.disabled = true),
  };
}

/* --------------------------------- table --------------------------------- */

function tableQ(q: Extract<Question, { type: "table" }>, onChange: () => void): MountedQuestion {
  const options = shuffled([...new Set(q.options)]);
  const selects = q.rows.map(() => {
    const sel = h("select", { class: "pf-select", onchange: onChange }, h("option", { value: "" }, "wählen …"),
      ...options.map((o) => h("option", { value: o }, o))) as HTMLSelectElement;
    return sel;
  });

  const el = h(
    "div",
    null,
    h("h2", { class: "pf-q-title", html: inline(q.question) }),
    h(
      "table",
      { class: "pf-table" },
      h("thead", null, h("tr", null, h("th", null, q.columns[0]), h("th", null, q.columns[1]))),
      h(
        "tbody",
        null,
        ...q.rows.map((r, i) =>
          h("tr", null, h("td", { class: "given" }, r.given), h("td", null, selects[i]))
        )
      )
    )
  );

  return {
    el,
    ready: () => selects.every((s) => s.value !== ""),
    read: () => ({ texts: selects.map((s) => s.value) }),
    reveal: () => {
      selects.forEach((s, i) => {
        const good = s.value === q.rows[i].correct;
        s.classList.add(good ? "good" : "bad");
        if (!good) s.value = q.rows[i].correct;
      });
    },
    lock: () => selects.forEach((s) => (s.disabled = true)),
  };
}
